import { Layer } from "@carbon/react";
import React from "react";

import { ProgressStepModal, useProgressStepModal } from "src/components/Modal";
import { useToast } from "src/components/Toasts/ToastContainer.tsx";
import { mapToModalError } from "src/errors";
import { getRulesQuery } from "src/graphql/anti-fraud/queries/rules/GetRulesQuery.ts";
import { CreateLinkedCaseRule } from "src/graphql/anti-fraud/queries/rules/mutations/CreateLinkedCaseRuleMutation.ts";
import { usePerformMutation } from "src/graphql/utils";
import { useT } from "src/internationalization";
import { mapToApiOperatorCode } from "src/mappers/operator.mappers";
import { Rule } from "src/types/rule";
import { cn } from "src/utils/string.utils";

import { DetailsFormData, DetailsForm } from "../DetailsForm";
import { useDetailsForm } from "../DetailsForm/useDetailsForm";

import { CaseTypeSelection } from "./CaseTypeSelection/CaseTypeSelection";
import { useCaseTypeSelection } from "./CaseTypeSelection/useCaseTypeSelection";
import { IdentifierForm, useIdentifierForm } from "./IdentifierForm";

export type CreateRuleModalProps = {
  onClose: () => void;
  caseTypeCode: string;
  rules: Rule[];
};

export const CreateLinkedCaseRuleModal: React.FC<CreateRuleModalProps> = ({
  onClose,
  caseTypeCode,
  rules,
}) => {
  const identifierFormControl = useIdentifierForm();
  const caseTypeSelectionControl = useCaseTypeSelection();
  const detailsFormControl = useDetailsForm({ rules });

  const { showSuccessToast } = useToast();
  const t = useT();

  const {
    performMutation: createLinkedCase,
    loading,
    error,
  } = usePerformMutation(CreateLinkedCaseRule, {
    onSuccess: () => {
      showSuccessToast(t("page.rules.modal.create.toast.success.message"));
      onClose();
    },
    mutationOption: {
      refetchQueries: [getRulesQuery],
      awaitRefetchQueries: true,
    },
  });

  const submit = async (data: DetailsFormData) => {
    const identifier = identifierFormControl.methods.getValues("identifier")!;
    const secondaryCriteriaList = identifierFormControl.secondaryCriteriaList;
    const selectedCaseType = caseTypeSelectionControl.selectedCaseType;

    await createLinkedCase({
      name: data.name,
      code: data.code,
      description: data.description,
      caseTypeCode: caseTypeCode,
      linkedCaseType: selectedCaseType?.code ?? null,
      identifierPath: {
        elementPath: [identifier.elementCode],
        identifierCode: identifier.identifierCode,
        origin: identifier.clientType === "customer" ? "CustomerOrigin" : "CaseOrigin",
      },
      linkedCaseConditions: secondaryCriteriaList.map((criteria) => ({
        operatorCode: criteria.operator?.code ? mapToApiOperatorCode(criteria.operator.code) : "",
        propertyPath: {
          elementPath: criteria.secondProperty.elementCode
            ? [criteria.secondProperty.elementCode]
            : [],
          propertyCode: criteria.secondProperty.propertyCode ?? "",
          origin: null,
        },
        value: criteria.value ?? "",
        listCondition: null,
      })),
    });
  };

  const { control: stepsControl, modalProps } = useProgressStepModal([
    {
      title: t("page.rules.modal.create.linkedCase.form.condition.title"),
      type: "identifier",
      invalid: identifierFormControl.hasError,
      disabled: false,
      submit: identifierFormControl.submit(() => {
        stepsControl.goNextStep();
      }),
      focus: "#identifier",
    },
    {
      title: t("page.rules.modal.create.linkedCase.form.caseType.title"),
      type: "caseType",
      invalid: caseTypeSelectionControl.hasError,
      disabled: false,
      submit: caseTypeSelectionControl.submit(() => {
        stepsControl.goNextStep();
      }),
      focus: "#case-type-selection",
    },
    {
      title: t("page.rules.modal.create.linkedCase.form.details.title"),
      type: "details",
      invalid: detailsFormControl.hasError,
      disabled: false,
      submit: detailsFormControl.submit(submit),
      focus: "#name",
    },
  ]);

  return (
    <ProgressStepModal
      loading={loading}
      kind="vertical"
      error={mapToModalError(error)}
      initialFocus="#identifier"
      title={t("page.rules.modal.create.linkedCase.title")}
      confirmButtonText={t("general.actions.create")}
      onClose={onClose}
      {...modalProps}
    >
      <Layer level={1} className="flex flex-col h-full min-h-[540px] bg-layer01">
        <div
          className={cn("flex flex-col h-full px-5", {
            hidden: !stepsControl.isStep("identifier"),
          })}
        >
          <IdentifierForm
            identifierFormControl={identifierFormControl}
            caseTypeCode={caseTypeCode}
          />
        </div>
        <div
          className={cn("flex flex-col h-full px-5", {
            hidden: !stepsControl.isStep("caseType"),
          })}
        >
          <CaseTypeSelection methods={caseTypeSelectionControl.methods} />
        </div>
        <div
          className={cn("flex flex-col px-5 h-full", {
            hidden: !stepsControl.isStep("details"),
          })}
        >
          <DetailsForm
            detailsFormControl={detailsFormControl}
            identifier={identifierFormControl.methods.getValues("identifier")!}
            secondaryCriteriaList={identifierFormControl.secondaryCriteriaList}
            selectedCaseTypes={
              caseTypeSelectionControl.selectedCaseType
                ? [caseTypeSelectionControl.selectedCaseType]
                : []
            }
          />
        </div>
      </Layer>
    </ProgressStepModal>
  );
};
